import {IEventManagerWithAsync, IService, SystemComponentType} from '@gongxigames/framework/core/types';
import {CoreDI, CoreSettings} from '@gongxigames/framework/core/configs';
import {CoreEventsMap} from '@gongxigames/framework/core/events';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {ReasonID} from '@gongxigames/framework/core/types';
import {strictCoreEmitter} from '@gongxigames/framework/core/globals';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

@injectable()
export class KeyboardModule implements IService {
    public static NAME: symbol = CoreDI.KeyboardModule;
    public static readonly SPACEBAR_CODE: number = 32;
    public static readonly ENTER_CODE: number = 13;

    public type: SystemComponentType = 'service';
    private _disabledReasons: any = [];
    private _enabled = true;
    private _actionsByKeyboardDisabled = false;

    @inject(CoreDI.IEventManagerWithAsync)
    private _eventManager!: IEventManagerWithAsync<CoreEventsMap>;

    @inject(CoreDI.Settings)
    protected _settings!: CoreSettings;

    public init(): void {
        document.body.onkeydown = this.onKeyDown.bind(this);
        document.body.onkeyup = this.onKeyUp.bind(this);
        this._actionsByKeyboardDisabled = this._settings.get('actionsByKeyboardDisabled') || false;

        this.setupEvents();
    }

    public destroy(): void {
        document.body.onkeydown = null;
        document.body.onkeyup = null;
    }

    public enable(reason: symbol): void {
        const index = this._disabledReasons.indexOf(reason);

        if (index !== -1) {
            this._disabledReasons.splice(index, 1);

            if (!this._disabledReasons.length) {
                this._enabled = true;
            }
        }
    }

    public disable(reason: symbol): void {
        if (this._disabledReasons.indexOf(reason) === -1) {
            this._disabledReasons.push(reason);

            this._enabled = false;
        }
    }

    private setupEvents(): void {
        this._eventManager.on('General.UI.ENABLE_KEYBOARD', (reason: ReasonID) => this.enable(reason), this);
        this._eventManager.on('General.UI.DISABLE_KEYBOARD', (reason: ReasonID) => this.disable(reason), this);
    }

    private onKeyDown(e: any): void {
        if (this._enabled) {
            if (isSpacebarOrEnterButton(e.keyCode) && !this._actionsByKeyboardDisabled) {
                this._eventManager.emit('General.UI.SPACEBAR_DOWN');
                strictUiEmitter.emit('General.UI.SPACEBAR_DOWN');
            }
            this._eventManager.emit('General.UI.KEY_DOWN', e.keyCode);
            strictCoreEmitter.emit('CORE.input.keydown', e);
        }
    }

    private onKeyUp(e: any): void {
        if (this._enabled) {
            if (isSpacebarOrEnterButton(e.keyCode) && !this._actionsByKeyboardDisabled) {
                this._eventManager.emit('General.UI.SPACEBAR_UP');
                strictUiEmitter.emit('General.UI.SPACEBAR_UP');
            }
            this._eventManager.emit('General.UI.KEY_UP', e.keyCode);
            strictCoreEmitter.emit('CORE.input.keyup', e);
        }
    }
}

function isSpacebarOrEnterButton(keyCode: number): boolean {
    return keyCode === KeyboardModule.SPACEBAR_CODE || keyCode === KeyboardModule.ENTER_CODE;
}
